import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { postLoginPath } from '../utils/supportAccess';
import api from '../utils/api';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import LifecycleLoop from './login/LifecycleLoop';
import './login/login.css';

const OTP_COOLDOWN_SECONDS = 60;

export default function Login() {
  const [emailView, setEmailView] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const { login } = useAuth();
  const navigate = useNavigate();
  const pageRef = useRef(null);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = setInterval(() => {
      setResendCooldown((value) => (value > 1 ? value - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const resetForgotState = () => {
    setEmailView('login');
    setOtp('');
    setNewPassword('');
    setConfirmPassword('');
    setResendCooldown(0);
    setSuccess('');
    setError('');
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      await login(email, password);
      navigate(postLoginPath());
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Login failed';
      setError(msg === 'Network Error' ? 'Cannot reach server. Check if backend is running.' : msg);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const { data } = await api.post('/auth/forgot-password/request', { email });
      setEmailView('reset');
      setResendCooldown(OTP_COOLDOWN_SECONDS);
      setSuccess(data.message || 'Verification code sent to your email.');
    } catch (err) {
      const retryAfter = err.response?.data?.retryAfterSeconds;
      if (retryAfter) setResendCooldown(retryAfter);
      setError(err.response?.data?.message || 'Unable to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.post('/auth/forgot-password/reset', {
        email,
        otp,
        new_password: newPassword,
      });
      setSuccess(data.message || 'Password updated. You can sign in now.');
      setPassword('');
      setOtp('');
      setNewPassword('');
      setConfirmPassword('');
      setEmailView('login');
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to reset password');
    } finally {
      setLoading(false);
    }
  };

  const onMove = (e) => {
    const el = pageRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty('--mx', ((e.clientX - r.left) / r.width - 0.5).toFixed(3));
    el.style.setProperty('--my', ((e.clientY - r.top) / r.height - 0.5).toFixed(3));
  };

  const heading = emailView === 'login' ? 'Welcome back' : emailView === 'forgot' ? 'Reset your password' : 'Check your inbox';
  const sub = emailView === 'login'
    ? 'Sign in to the Rentfoxxy operations CRM.'
    : emailView === 'forgot' ? 'We will email you a 6-digit verification code.' : `We sent a code to ${email}.`;
  const Submit = ({ busy, idle, children }) => (
    <button type="submit" disabled={loading} className="lp-submit">
      {loading ? <><span className="lp-spinner" />{busy}</> : <>{idle}{children}</>}
    </button>
  );

  return (
    <div className="lp-page" ref={pageRef}>
      <section className="lp-visual" onMouseMove={onMove} aria-label="How a laptop moves through Rentfoxxy">
        <span className="lp-glow lp-glow--cyan" />
        <span className="lp-glow lp-glow--orange" />
        <div className="lp-visual__top">
          <img src="/rentfoxxy-logo-long.png" alt="Rentfoxxy" className="lp-brandlogo" />
          <span className="lp-visual__hint">Hover a step to see who does what</span>
        </div>
        <div className="lp-stage">
          <LifecycleLoop />
        </div>
        <div className="lp-visual__foot">
          <div className="lp-fact"><b>One TTSPL code</b><span>follows each laptop for life</span></div>
          <div className="lp-fact"><b>8 teams, one record</b><span>procure to support, no hand-offs lost</span></div>
          <div className="lp-fact"><b>Rent, sale, demo</b><span>Rentfoxxy and gorefurbo on one floor</span></div>
        </div>
      </section>

      <main className="lp-panel">
        <div className="lp-card">
        <p className="lp-panel__eyebrow">Operations CRM</p>
        <h1>{heading}</h1>
        <p className="lp-panel__sub">{sub}</p>

        {error && <div className="lp-alert lp-alert--error" role="alert">{error}</div>}
        {success && <div className="lp-alert lp-alert--ok" role="status">{success}</div>}

        {emailView === 'login' && (
          <form onSubmit={handleEmailLogin} className="lp-form">
            <div className="lp-field">
              <label htmlFor="lp-email">Work email</label>
              <input id="lp-email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@rentfoxxy.com" required className="lp-input" />
            </div>
            <div className="lp-field">
              <label htmlFor="lp-password">
                Password
                <button type="button" className="lp-link" onClick={() => { setEmailView('forgot'); setError(''); setSuccess(''); }}>
                  Forgot password?
                </button>
              </label>
              <div className="lp-field__wrap">
                <input id="lp-password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" value={password}
                  onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required className="lp-input lp-input--pad" />
                <button type="button" className="lp-reveal" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>
            <Submit busy="Signing in…" idle="Sign in"><ArrowRight size={16} /></Submit>
          </form>
        )}

        {emailView === 'forgot' && (
          <form onSubmit={handleRequestOtp} className="lp-form">
            <div className="lp-field">
              <label htmlFor="lp-forgot">Work email</label>
              <input id="lp-forgot" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@rentfoxxy.com" required autoFocus className="lp-input" />
            </div>
            <Submit busy="Sending code…" idle="Send verification code" />
            <button type="button" className="lp-back" onClick={resetForgotState}>
              <ArrowLeft size={14} /> Back to sign in
            </button>
          </form>
        )}

        {emailView === 'reset' && (
          <form onSubmit={handleResetPassword} className="lp-form">
            <div className="lp-field">
              <label htmlFor="lp-otp">Verification code</label>
              <input id="lp-otp" type="text" inputMode="numeric" pattern="\d{6}" maxLength={6} value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000" required autoFocus autoComplete="one-time-code" className="lp-input lp-input--code" />
              <p className="lp-hint">
                <span>Code expires in 10 minutes</span>
                <button type="button" className="lp-link" disabled={loading || resendCooldown > 0} onClick={handleRequestOtp}>
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                </button>
              </p>
            </div>
            <div className="lp-field">
              <label htmlFor="lp-new">New password</label>
              <div className="lp-field__wrap">
                <input id="lp-new" type={showNewPassword ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 8 characters" required minLength={8} autoComplete="new-password" className="lp-input lp-input--pad" />
                <button type="button" className="lp-reveal" onClick={() => setShowNewPassword(!showNewPassword)}>
                  {showNewPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>
            <div className="lp-field">
              <label htmlFor="lp-confirm">Confirm new password</label>
              <input id="lp-confirm" type={showNewPassword ? 'text' : 'password'} value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Re-enter password" required minLength={8}
                autoComplete="new-password" className="lp-input" />
            </div>
            <Submit busy="Updating password…" idle="Reset password" />
            <button type="button" className="lp-back" onClick={() => {
              setEmailView('forgot'); setOtp(''); setNewPassword(''); setConfirmPassword(''); setError(''); setSuccess('');
            }}>
              <ArrowLeft size={14} /> Use a different email
            </button>
          </form>
        )}

        <p className="lp-panel__foot">Staff accounts are issued by your admin · © Rentfoxxy</p>
        </div>
      </main>
    </div>
  );
}
